'use client'

import { useEffect, useState } from 'react'
import { useRouter, usePathname } from 'next/navigation'
import type { User } from '@supabase/supabase-js'
import { useSupabase } from '@/hooks/useSupabase'
import { NotificationsProvider } from '@/layout/provider/NotificationsProvider'
import ClientHeaderClient from '@/layout/header/ClientHeaderClient'

export default function ClientLayout({
  children,
}: Readonly<{
  children: React.ReactNode
}>) {
  const router = useRouter()
  const pathname = usePathname()
  const { supabase, user, loading: authLoading } = useSupabase()

  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [isCheckingClient, setIsCheckingClient] = useState(false)

  // 비로그인 상태에서 클라이언트 페이지 접근 시 /auth로 리다이렉트
  useEffect(() => {
    if (authLoading) return

    if (!user) {
      const checkSessionAndRedirect = async () => {
        setIsCheckingAuth(true)

        try {
          // getUser()를 사용하여 토큰 검증과 함께 사용자 정보 확인 (재시도 포함)
          let sessionUser: User | null = null
          const maxRetries = 2

          for (let i = 0; i <= maxRetries; i++) {
            const result = await supabase.auth.getUser()
            sessionUser = result.data.user
            if (sessionUser) break
            if (i < maxRetries) await new Promise((r) => setTimeout(r, 300))
          }

          // 사용자가 없으면 /auth로 리다이렉트
          if (!sessionUser) {
            const currentPath = pathname || '/'
            router.push(`/auth?redirect=${encodeURIComponent(currentPath)}&role=client`)
            return
          }

          // 사용자가 있으면 useSupabase가 user를 채울 때까지 대기
          setIsCheckingAuth(false)
        } catch (error) {
          console.error('[client layout] 세션 확인 실패:', error)
          const currentPath = pathname || '/'
          router.push(`/auth?redirect=${encodeURIComponent(currentPath)}&role=client`)
        } finally {
          setIsCheckingAuth(false)
        }
      }

      checkSessionAndRedirect()
    } else {
      setIsCheckingAuth(false)
    }
  }, [authLoading, user, router, pathname, supabase])

  // 기업 계정 확인 (로그인된 사용자만)
  useEffect(() => {
    if (authLoading || !user || isCheckingAuth) return

    let cancelled = false

    const checkClientAccount = async () => {
      setIsCheckingClient(true)

      try {
        const { data: clientData, error } = await supabase
          .from('client')
          .select('user_id, company_name, contact_person, contact_phone')
          .eq('user_id', user.id)
          .maybeSingle()

        if (cancelled) return

        // 조회 에러는 온보딩 유도 (보수적으로 처리)
        if (error) {
          console.error('기업 계정 확인 실패:', error)
          router.push('/onboarding/enterprise/company')
          return
        }

        // 기업 계정이 없거나 필수정보 누락이면 온보딩
        if (!clientData?.company_name || !clientData?.contact_person || !clientData?.contact_phone) {
          router.push('/onboarding/enterprise/company')
          return
        }
      } catch (error) {
        if (cancelled) return
        console.error('기업 계정 확인 실패:', error)
        router.push('/onboarding/enterprise/company')
      } finally {
        if (!cancelled) setIsCheckingClient(false)
      }
    }

    checkClientAccount()

    return () => {
      cancelled = true
    }
  }, [authLoading, user, router, supabase, isCheckingAuth])

  /**
   * ✅ FreelancerLayout과 동일한 “컨테이너/여백” 규격
   * - 페이지(children)에서는 mx-auto/max-w/px/py를 절대 들고 있지 않도록 통일
   */
  const Shell = ({ children }: { children: React.ReactNode }) => (
    <>
      {/* ✅ Header는 최상단 app/layout.tsx에서 한 번만 렌더링됨 */}
      <NotificationsProvider enabled>
        <main className="w-full min-h-[calc(100vh-64px)] pt-[64px] md:pt-[65px]">
          {/* ✅ 컨테이너는 여기서만 통일 (FreelancerLayout과 동일) */}
          <div className="w-full px-4 sm:px-6 lg:px-8 md:max-w-[90%] md:mx-auto">
            {/* ✅ 상단 여백(레이아웃이 관리) */}
            <div className="w-full py-8">
              {/* ✅ children 폭 강제 */}
              <div className="w-full">{children}</div>
            </div>
          </div>
        </main>
      </NotificationsProvider>
    </>
  )

  if (authLoading || isCheckingAuth || isCheckingClient) {
    return (
      <Shell>
        <div className="flex items-center justify-center min-h-[320px] w-full">
          <div className="text-gray-600 text-sm">로딩 중...</div>
        </div>
      </Shell>
    )
  }

  return <Shell>{children}</Shell>
}
